// Slice 6: Investor Readiness page.
import { useEffect, useState } from "react";
import { Gauge } from "lucide-react";
import { Link } from "wouter";
import { useCohort, fp } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface Component {
  id: number; componentKey: string; label: string;
  maxScore: number; currentScore: number; percent: number;
  gapDescription: string; closesWith: string;
}

export default function FounderReadiness() {
  const { slug } = useCohort();
  const [data, setData] = useState<{ totalCurrent: number; totalMax: number; percent: number; components: Component[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}api/founder-portal/readiness`, { headers: auth() });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  if (!data) return <div className="p-12 text-red-400 text-sm">Could not load readiness.</div>;

  const ringColor = data.percent >= 70 ? "text-green-400" : data.percent >= 40 ? "text-[#FF5C00]" : "text-white/60";

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Investor Readiness</div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">How investor-ready you are right now</h1>
        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
          Eight parts make up your score. Each part tells you what is missing and which kind of file or document closes it.
        </p>
      </div>

      {/* Score */}
      <div className="bg-gradient-to-br from-[#FF5C00]/10 via-[#FF5C00]/5 to-transparent border border-[#FF5C00]/30 rounded-xl p-6 mb-6 flex items-center gap-6">
        <div className={`text-6xl font-bold ${ringColor} tabular-nums`}>{data.percent}<span className="text-2xl text-white/60">%</span></div>
        <div className="flex-1">
          <div className="text-sm text-white/75 mb-1">{data.totalCurrent} of {data.totalMax} points</div>
          <div className="w-full h-2 bg-white/[0.08] rounded-full overflow-hidden">
            <div className="h-full bg-[#FF5C00] rounded-full" style={{ width: `${data.percent}%` }} />
          </div>
          <Link href={fp(slug, "proof")} className="text-xs text-[#FF5C00] hover:text-[#FF5C00]/80 mt-2 inline-block">
            Add a proof to move your score
          </Link>
        </div>
      </div>

      {/* Components */}
      <div className="space-y-2.5">
        {data.components.map(c => (
          <div key={c.id} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold mb-0.5">{c.label}</h3>
                <div className="text-[11px] text-white/60 tabular-nums">{c.currentScore} / {c.maxScore} points</div>
              </div>
              <div className={`text-sm font-bold tabular-nums ${c.percent >= 100 ? "text-green-400" : c.percent > 0 ? "text-[#FF5C00]" : "text-white/60"}`}>
                {c.percent}%
              </div>
            </div>
            <div className="w-full h-1 bg-white/[0.06] rounded-full overflow-hidden mb-3">
              <div className={`h-full rounded-full ${c.percent >= 100 ? "bg-green-400" : "bg-[#FF5C00]"}`} style={{ width: `${c.percent}%` }} />
            </div>
            {c.percent < 100 && (
              <>
                <p className="text-xs text-white/65 leading-relaxed mb-1">{c.gapDescription}</p>
                <p className="text-[11px] text-white/60 leading-relaxed"><span className="text-white/60 font-medium">Closes with: </span>{c.closesWith}</p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
